import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangleIcon, Loader2Icon, SaveIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel, FieldGroup } from '@/components/ui/field';
import { updateSalon, createSalon, type SalonRecord } from '@/lib/auth-client';
import { cn } from '@/lib/utils';

const emptyStringToUndefined = (value: unknown) => {
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }

  return value;
};

const requiredNumberField = (label: string, maxValue?: number) => z.preprocess(
  emptyStringToUndefined,
  z.coerce.number({
    error: `${label} est requis`,
  })
    .min(0, 'Valeur positive')
    .refine((value) => maxValue === undefined || value <= maxValue, {
      message: maxValue === undefined ? 'Valeur invalide' : `Maximum ${maxValue}%`,
    }),
);

const salonSchema = z.object({
  name: z.string().min(2, "Le nom est requis"),
  ville: z.string().optional().nullable(),
  type: z.enum(['BtoB', 'BtoC', 'Les 2'], {
    error: 'Le type est requis',
  }),
  status: z.enum(['Planifié', 'Active', 'Inactive', 'Terminé']),
  startDate: z.string().min(1, 'La date de début est requise'),
  endDate: z.string().min(1, 'La date de fin est requise'),
  lot1Qty: requiredNumberField('La quantité du lot 1'),
  lot1WinPercent: requiredNumberField('La probabilité du lot 1', 100),
  lot2Qty: requiredNumberField('La quantité du lot 2'),
  lot2WinPercent: requiredNumberField('La probabilité du lot 2', 100),
  // lot1Remaining and lot2Remaining are handled by backend or only editable in 'update'
  lot1Remaining: z.number().optional(),
  lot2Remaining: z.number().optional(),
});

type SalonFormInput = z.input<typeof salonSchema>;
type SalonFormOutput = z.output<typeof salonSchema>;

interface SalonFormProps {
  regionId: string;
  existingSalons?: SalonRecord[];
  salon?: SalonRecord;
  onSuccess: () => void;
  onCancel?: () => void;
}

function rangesOverlap(left: { startDate?: string | null; endDate?: string | null }, right: { startDate?: string | null; endDate?: string | null }) {
  const leftStart = left.startDate ? new Date(left.startDate) : left.endDate ? new Date(left.endDate) : null;
  const leftEnd = left.endDate ? new Date(left.endDate) : left.startDate ? new Date(left.startDate) : null;
  const rightStart = right.startDate ? new Date(right.startDate) : right.endDate ? new Date(right.endDate) : null;
  const rightEnd = right.endDate ? new Date(right.endDate) : right.startDate ? new Date(right.startDate) : null;

  if (!leftStart || !leftEnd || !rightStart || !rightEnd) {
    return false;
  }

  return leftStart.getTime() <= rightEnd.getTime() && rightStart.getTime() <= leftEnd.getTime();
}

function getAutomaticSalonStatus(startDate?: string | null) {
  if (!startDate) {
    return 'Planifié' as const;
  }

  return new Date(startDate).getTime() <= Date.now() ? 'Active' as const : 'Planifié' as const;
}

function numberFieldRegistration(value: string) {
  return value === '' ? undefined : Number(value);
}

export function SalonForm({ regionId, existingSalons = [], salon, onSuccess, onCancel }: SalonFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCreation = !salon;
  const isTerminated = salon?.status === 'Terminé';

  const form = useForm<SalonFormInput, undefined, SalonFormOutput>({
    resolver: zodResolver(salonSchema),
    defaultValues: {
      name: salon?.name || '',
      ville: salon?.ville || '',
      type: salon?.type || undefined,
      status: salon?.status || 'Planifié',
      startDate: salon?.startDate ? new Date(salon.startDate).toISOString().split('T')[0] : '',
      endDate: salon?.endDate ? new Date(salon.endDate).toISOString().split('T')[0] : '',
      lot1Qty: salon ? Number(salon.lot1Qty) : undefined,
      lot1WinPercent: salon ? Number(salon.lot1WinPercent) : undefined,
      lot2Qty: salon ? Number(salon.lot2Qty) : undefined,
      lot2WinPercent: salon ? Number(salon.lot2WinPercent) : undefined,
      lot1Remaining: salon?.lot1Remaining,
      lot2Remaining: salon?.lot2Remaining,
    },
  });

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const selectedStatus = form.watch('status');
  const automaticStatus = useMemo(() => getAutomaticSalonStatus(startDate), [startDate]);
  const overlappingSalons = useMemo(
    () => existingSalons
      .filter((item) => item.id !== salon?.id && item.status !== 'Terminé')
      .filter((item) => rangesOverlap({
        startDate,
        endDate,
      }, {
        startDate: item.startDate ? new Date(item.startDate).toISOString().split('T')[0] : null,
        endDate: item.endDate ? new Date(item.endDate).toISOString().split('T')[0] : null,
      })),
    [endDate, existingSalons, salon?.id, startDate],
  );
  const shouldUseSecondaryQr = overlappingSalons.length >= 1;
  const exceedsAvailableQrCodes = overlappingSalons.length >= 2;

  useEffect(() => {
    if (selectedStatus === 'Inactive' || selectedStatus === 'Terminé') {
      return;
    }

    if (selectedStatus !== automaticStatus) {
      form.setValue('status', automaticStatus, { shouldDirty: true });
    }
  }, [automaticStatus, form, selectedStatus]);

  async function onSubmit(data: SalonFormOutput) {
    if (isTerminated) return;
    if (exceedsAvailableQrCodes) {
      toast.error("Deux salons se chevauchent déjà sur cette période. Impossible d'affecter un troisième QR code.");
      return;
    }
    setIsSubmitting(true);
    
    // Format dates properly
    const payload = {
      ...data,
      regionId,
      startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
      endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
    };

    try {
      if (isCreation) {
        const { error } = await createSalon(payload);
        if (error) throw error;
        toast.success("Salon créé avec succès");
      } else {
        const { error } = await updateSalon(salon!.id, payload);
        if (error) throw error;
        toast.success("Salon mis à jour avec succès");
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(isCreation ? "Erreur lors de la création" : "Erreur lors de la sauvegarde");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="rounded-[var(--radius-surface-2xl)] border border-border bg-card p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-foreground mb-6">
          {isCreation ? "Nouveau Salon" : "Paramètres du Salon"}
          {isTerminated && <span className="ml-3 text-xs font-normal text-muted-foreground">(En lecture seule - Terminé)</span>}
        </h2>

        {isCreation && shouldUseSecondaryQr ? (
          <div className={cn(
            'mb-6 rounded-[var(--radius-surface-3xl)] border p-4 text-sm',
            exceedsAvailableQrCodes
              ? 'border-destructive/30 bg-destructive/5 text-destructive'
              : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200',
          )}>
            <div className="flex items-start gap-3">
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">
                  {exceedsAvailableQrCodes ? 'Chevauchement impossible avec les QR codes disponibles' : 'Chevauchement détecté sur la période sélectionnée'}
                </p>
                <p>
                  {exceedsAvailableQrCodes
                    ? 'Cette période recoupe déjà deux salons. Modifie les dates avant de créer ce salon.'
                    : 'Il faudra utiliser le QR code 2 de secours pour ce salon, car un autre salon est déjà prévu sur cette période.'}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        
        <div className="grid gap-6 md:grid-cols-2">
          <FieldGroup>
            <FieldLabel htmlFor="name">Nom du salon <span className="text-destructive">*</span></FieldLabel>
            <Field>
              <Input
                id="name"
                {...form.register("name")}
                disabled={isTerminated}
                placeholder="Salon de la Mutualité de Marseille"
                className={cn('h-11', form.formState.errors.name && 'border-destructive')}
              />
            </Field>
            {form.formState.errors.name && (
              <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.name.message}</p>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Statut <span className="text-destructive">*</span></FieldLabel>
            <Field>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isTerminated || selectedStatus !== 'Inactive'}
                  onClick={() => form.setValue('status', automaticStatus, { shouldDirty: true })}
                  className={cn(
                    'flex-1 h-11 opacity-100',
                    selectedStatus !== 'Inactive'
                      ? 'border-black bg-black text-white hover:border-black hover:bg-black hover:text-white dark:border-border dark:bg-muted dark:text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted/30',
                  )}
                >
                  {automaticStatus}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isTerminated}
                  onClick={() => form.setValue('status', selectedStatus === 'Inactive' ? automaticStatus : 'Inactive', { shouldDirty: true })}
                  className={cn(
                    'flex-1 h-11',
                    selectedStatus === 'Inactive'
                      ? 'border-black bg-black text-white hover:border-black/90 hover:bg-black/90 hover:text-white dark:border-border dark:bg-muted dark:text-foreground'
                      : 'border-border hover:border-foreground/20 hover:bg-muted/30',
                  )}
                >
                  Inactif
                </Button>
              </div>
            </Field>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="ville">Ville</FieldLabel>
            <Field>
              <Input
                id="ville"
                {...form.register("ville")}
                disabled={isTerminated}
                placeholder="Marseille"
                className={cn('h-11', form.formState.errors.ville && 'border-destructive')}
              />
            </Field>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Type (Cible) <span className="text-destructive">*</span></FieldLabel>
            <Field>
              <div className="flex gap-2">
                {(['BtoB', 'BtoC', 'Les 2'] as const).map((t) => (
                  <Button 
                    key={t}
                    type="button" 
                    variant="outline"
                    disabled={isTerminated}
                    onClick={() => form.setValue('type', t, { shouldDirty: true })}
                    className={cn(
                      'flex-1 h-11',
                      form.watch('type') === t
                        ? 'border-black bg-black text-white hover:border-black/90 hover:bg-black/90 hover:text-white dark:border-border dark:bg-muted dark:text-foreground'
                        : 'border-border hover:border-foreground/20 hover:bg-muted/30',
                    )}
                  >
                    {t}
                  </Button>
                ))}
              </div>
            </Field>
            {form.formState.errors.type && (
              <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.type.message}</p>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="startDate">Date de début <span className="text-destructive">*</span></FieldLabel>
            <Field>
              <Input
                id="startDate"
                type="date"
                {...form.register("startDate")}
                disabled={isTerminated}
                placeholder="2026-04-08"
                className={cn('h-11', form.formState.errors.startDate && 'border-destructive')}
              />
            </Field>
            {form.formState.errors.startDate && (
              <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.startDate.message}</p>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="endDate">Date de fin <span className="text-destructive">*</span></FieldLabel>
            <Field>
              <Input
                id="endDate"
                type="date"
                {...form.register("endDate")}
                disabled={isTerminated}
                placeholder="2026-04-12"
                className={cn('h-11', form.formState.errors.endDate && 'border-destructive')}
              />
            </Field>
            {form.formState.errors.endDate && (
              <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.endDate.message}</p>
            )}
          </FieldGroup>
        </div>
      </div>

      <div className="rounded-[var(--radius-surface-2xl)] border border-border bg-card p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-foreground mb-6">Configuration du Jeu (Lots)</h2>
        
        <div className="grid gap-6 md:grid-cols-2">
          {/* Lot 1 */}
          <div className="space-y-4 p-4 border rounded-xl bg-accent/5">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Lot 1</h3>
              {!isCreation && salon && (
                <span className="text-xs text-muted-foreground bg-background px-2 py-0.5 rounded border">
                  Stock: {salon.lot1Remaining} / {salon.lot1Qty}
                </span>
              )}
            </div>
            
            <FieldGroup>
              <FieldLabel htmlFor="lot1Qty">Quantité totale <span className="text-destructive">*</span></FieldLabel>
              <Field>
                <Input
                  id="lot1Qty"
                  type="number"
                  {...form.register("lot1Qty", { setValueAs: numberFieldRegistration })}
                  disabled={isTerminated}
                  placeholder="Ex. 150"
                  className={cn('h-11', form.formState.errors.lot1Qty && 'border-destructive')}
                />
              </Field>
              {form.formState.errors.lot1Qty && (
                <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.lot1Qty.message}</p>
              )}
            </FieldGroup>
            
            <FieldGroup>
              <FieldLabel htmlFor="lot1WinPercent">Probabilité (%) <span className="text-destructive">*</span></FieldLabel>
              <Field>
                <Input
                  id="lot1WinPercent"
                  type="number"
                  {...form.register("lot1WinPercent", { setValueAs: numberFieldRegistration })}
                  disabled={isTerminated}
                  placeholder="Ex. 10"
                  className={cn('h-11', form.formState.errors.lot1WinPercent && 'border-destructive')}
                />
              </Field>
              {form.formState.errors.lot1WinPercent && (
                <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.lot1WinPercent.message}</p>
              )}
            </FieldGroup>
          </div>

          {/* Lot 2 */}
          <div className="space-y-4 p-4 border rounded-xl bg-accent/5">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Lot 2</h3>
              {!isCreation && salon && (
                <span className="text-xs text-muted-foreground bg-background px-2 py-0.5 rounded border">
                  Stock: {salon.lot2Remaining} / {salon.lot2Qty}
                </span>
              )}
            </div>
            
            <FieldGroup>
              <FieldLabel htmlFor="lot2Qty">Quantité totale <span className="text-destructive">*</span></FieldLabel>
              <Field>
                <Input
                  id="lot2Qty"
                  type="number"
                  {...form.register("lot2Qty", { setValueAs: numberFieldRegistration })}
                  disabled={isTerminated}
                  placeholder="Ex. 50"
                  className={cn('h-11', form.formState.errors.lot2Qty && 'border-destructive')}
                />
              </Field>
              {form.formState.errors.lot2Qty && (
                <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.lot2Qty.message}</p>
              )}
            </FieldGroup>
            
            <FieldGroup>
              <FieldLabel htmlFor="lot2WinPercent">Probabilité (%) <span className="text-destructive">*</span></FieldLabel>
              <Field>
                <Input
                  id="lot2WinPercent"
                  type="number"
                  {...form.register("lot2WinPercent", { setValueAs: numberFieldRegistration })}
                  disabled={isTerminated}
                  placeholder="Ex. 2"
                  className={cn('h-11', form.formState.errors.lot2WinPercent && 'border-destructive')}
                />
              </Field>
              {form.formState.errors.lot2WinPercent && (
                <p className="text-[0.8rem] font-medium text-destructive">{form.formState.errors.lot2WinPercent.message}</p>
              )}
            </FieldGroup>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-border pt-6">
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="h-11 px-6"
          >
            Annuler
          </Button>
        )}
        {!isTerminated && (
          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-11 min-w-32 border-black bg-black text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80"
          >
            {isSubmitting ? (
              <Loader2Icon className="mr-2 size-4 animate-spin" />
            ) : (
              <SaveIcon className="mr-2 size-4" />
            )}
            {isCreation ? "Créer le Salon" : "Enregistrer les modifications"}
          </Button>
        )}
      </div>
    </form>
  );
}
